import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PlayCircle, GraduationCap, LineChart, Users, Briefcase,
  ArrowRight, Sparkles, ShieldCheck,
} from "lucide-react";
import AppShell from "../components/AppShell";
import { Logo } from "../components/Logo";
import { isAuthenticated } from "../lib/auth";

const HIGHLIGHTS = [
  { icon: PlayCircle,    label: "Short videos" },
  { icon: GraduationCap, label: "Structured courses" },
  { icon: LineChart,     label: "Crypto predictions" },
  { icon: Users,         label: "Private community" },
  { icon: Briefcase,     label: "Placement help" },
];

export default function Landing() {
  const nav = useNavigate();
  const authed = isAuthenticated();
  const primaryHref = authed ? "/payment" : "/signup";

  return (
    <AppShell>
      <div className="min-h-screen bg-white flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 pt-4">
          <Logo size="sm" />
          <Link
            to={authed ? "/payment" : "/login"}
            data-testid="landing-login-link"
            className="h-9 px-4 rounded-full border border-neutral-200 flex items-center text-[12.5px] font-semibold text-neutral-800 hover:bg-neutral-50"
          >
            {authed ? "Dashboard" : "Login"}
          </Link>
        </div>

        {/* Hero */}
        <section className="px-6 pt-10 pb-6 relative overflow-hidden">
          <div className="absolute -right-24 -top-20 w-72 h-72 rounded-full bg-[#FF5A00]/12 blur-3xl pointer-events-none" />
          <div className="absolute -left-20 top-32 w-48 h-48 rounded-full bg-[#FF9A4A]/15 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFF0E6] text-brand text-[11px] font-bold tracking-wider">
              <span className="w-1.5 h-1.5 bg-brand rounded-full animate-pulse" />
              PRE-BOOKING · 51% OFF
            </div>

            <h1
              className="mt-5 font-display font-black text-[46px] tracking-tighter leading-[0.95]"
              data-testid="landing-hero-title"
              aria-label="Learn. Predict. Grow."
            >
              Learn.
              <br />
              <span className="text-brand-gradient">Predict.</span>
              <br />
              Grow.
            </h1>

            <p className="mt-4 text-[14.5px] text-neutral-600 leading-relaxed max-w-sm">
              One platform. Courses, crypto predictions, private community and placement support — all inside GGES Ventures.
            </p>
          </div>
        </section>

        {/* Attractive price card with big SAVE ribbon */}
        <section className="px-6 pt-2">
          <div className="relative rounded-3xl overflow-hidden border-2 border-brand/50 bg-gradient-to-br from-white via-[#FFF6EE] to-[#FFD9B8] p-6">
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-brand/20 blur-2xl" />
            {/* SAVE ribbon */}
            <div className="absolute -right-12 top-4 rotate-45 bg-brand text-white text-[11px] font-black tracking-widest px-14 py-1 shadow-lg">
              SAVE 51%
            </div>

            <div className="relative">
              <div className="text-[10px] font-bold uppercase tracking-widest text-brand">
                Pre-book today
              </div>

              {/* Original price crossed out - prominent */}
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[16px] text-neutral-500 line-through decoration-[2.5px] decoration-red-500/70 font-semibold tabular-nums">
                  $250
                </span>
                <span className="px-2 py-0.5 rounded-md bg-red-500/10 text-red-600 text-[10px] font-bold tracking-widest">
                  -51%
                </span>
              </div>

              {/* Final price - HUGE and gradient */}
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className="font-display font-black text-[52px] tracking-tighter tabular-nums leading-none text-brand-gradient"
                  data-testid="landing-price"
                >
                  $122.50
                </span>
                <span className="text-[13px] font-semibold text-emerald-600">You save $127.50</span>
              </div>

              <div className="mt-2 text-[12.5px] text-neutral-600 tabular-nums">
                Charged in INR ≈ <span className="font-semibold text-black">₹11,883</span>
                <span className="text-neutral-400 line-through ml-2">₹24,250</span>
              </div>
            </div>

            <div className="relative mt-4 flex items-center gap-2 text-[11.5px] text-neutral-600">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
              One-time · Lifetime access · Razorpay checkout
            </div>
          </div>
        </section>

        {/* Compact highlights strip */}
        <section className="px-6 pt-8">
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-neutral-500">
            What you get
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2.5" data-testid="landing-highlights">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <div
                  key={i}
                  data-testid={`landing-highlight-${i}`}
                  className="flex items-center gap-3 p-3 rounded-2xl border border-neutral-200 bg-white"
                >
                  <div className="w-8 h-8 rounded-lg bg-[#FFF0E6] text-brand flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span className="text-[13px] font-semibold text-black">{h.label}</span>
                </div>
              );
            })}
          </div>
        </section>

        {/* Launch date band */}
        <section className="px-6 pt-8 pb-32">
          <div className="p-4 rounded-2xl bg-black text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-brand" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">
                Launch day
              </div>
              <div className="text-[14px] font-semibold mt-0.5">10 August 2026 · IST</div>
            </div>
            <span className="text-[11px] font-bold text-brand">LIMITED SPOTS</span>
          </div>
        </section>

        {/* Fixed bottom CTA */}
        <div className="sticky-cta">
          <button
            onClick={() => nav(primaryHref)}
            data-testid="landing-prebook-btn"
            className="btn-brand btn-brand--pulse w-full h-11 flex items-center justify-center gap-2 text-[14px]"
          >
            Pre-book my seat
            <ArrowRight className="w-4 h-4" />
          </button>
          <div className="mt-1.5 text-center text-[10.5px] text-neutral-500 tabular-nums">
            10 August 2026 · Limited spots
          </div>
        </div>
      </div>
    </AppShell>
  );
}
